import React, { useState, useEffect } from 'react';
import { FaUserEdit, FaCalendarAlt } from 'react-icons/fa';
import { updateDoc, doc } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import { toast } from 'react-toastify';
import styles from './InsuranceModals.module.css';
import { useTheme } from '../../contexts/ThemeContext';

const EditInsuranceModal = ({ show, handleClose, selectedInsurance, onInsuranceUpdated }) => {
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState({
    employeeId: '',
    hasBES: false,
    accidentInsurance: {
      startDate: '',
      endDate: ''
    },
    complementaryHealth: {
      startDate: '',
      endDate: ''
    },
    malpractice: {
      startDate: '',
      endDate: ''
    },
    cost: '',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (show && selectedInsurance) {
      setFormData({
        employeeId: selectedInsurance.employeeId,
        hasBES: selectedInsurance.hasBES,
        accidentInsurance: {
          startDate: selectedInsurance.accidentInsurance?.startDate || '',
          endDate: selectedInsurance.accidentInsurance?.endDate || ''
        },
        complementaryHealth: {
          startDate: selectedInsurance.complementaryHealth?.startDate || '',
          endDate: selectedInsurance.complementaryHealth?.endDate || ''
        },
        malpractice: {
          startDate: selectedInsurance.malpractice?.startDate || '',
          endDate: selectedInsurance.malpractice?.endDate || ''
        },
        cost: selectedInsurance.cost || '',
      });
      // Reset errors when opening the modal
      setErrors({});
    }
  }, [show, selectedInsurance]);

  const handleInputChange = (field, value) => {
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: null
      });
    }
    
    setFormData(prev => {
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        return {
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: value
          }
        };
      }
      return {
        ...prev,
        [field]: value
      };
    });
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Optional: validate fields as needed
    if (formData.cost && isNaN(formData.cost)) {
      newErrors.cost = 'Maliyet sayısal bir değer olmalıdır';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error('Lütfen form hatalarını düzeltin');
      return;
    }
    
    try {
      const insuranceData = {
        ...formData,
        updatedAt: new Date()
      };

      await updateDoc(doc(db, 'Insurances', selectedInsurance.id), insuranceData);
      toast.success('Sigorta başarıyla güncellendi');
      handleClose();
      if (onInsuranceUpdated) onInsuranceUpdated();
    } catch (error) {
      console.error('Error updating insurance:', error);
      toast.error('Sigorta güncellenirken bir hata oluştu');
    }
  };

  if (!show) return null;

  return (
    <div 
      className={styles.modalOverlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <div 
        className={`${styles.modalContainer} ${isDarkMode ? 'dark-mode' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            <FaUserEdit className={styles.modalIcon} />
            Sigorta Düzenle
          </h2>
          <button
            className={styles.closeButton}
            onClick={handleClose}
            aria-label="Kapat"
          >
            ×
          </button>
        </div>

        <div className={styles.modalContent}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>BES:</label>
            <select
              value={formData.hasBES.toString()}
              onChange={(e) => handleInputChange('hasBES', e.target.value === 'true')}
              className={`${styles.formControl} ${styles.selectControl}`}
            >
              <option value="false">Hayır</option>
              <option value="true">Evet</option>
            </select>
          </div>

          <div className={styles.sectionTitle}>Ferdi Kaza Sigortası</div>
          
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              <FaCalendarAlt style={{ marginRight: '5px' }} /> Başlangıç Tarihi:
            </label>
            <input
              type="date"
              value={formData.accidentInsurance.startDate}
              onChange={(e) => handleInputChange('accidentInsurance.startDate', e.target.value)}
              className={styles.formControl}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              <FaCalendarAlt style={{ marginRight: '5px' }} /> Bitiş Tarihi:
            </label>
            <input
              type="date"
              value={formData.accidentInsurance.endDate}
              onChange={(e) => handleInputChange('accidentInsurance.endDate', e.target.value)}
              className={styles.formControl}
            />
          </div>

          <div className={styles.sectionTitle}>Tamamlayıcı Sağlık Sigortası</div>
          
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              <FaCalendarAlt style={{ marginRight: '5px' }} /> Başlangıç Tarihi:
            </label>
            <input
              type="date"
              value={formData.complementaryHealth.startDate}
              onChange={(e) => handleInputChange('complementaryHealth.startDate', e.target.value)}
              className={styles.formControl}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              <FaCalendarAlt style={{ marginRight: '5px' }} /> Bitiş Tarihi:
            </label>
            <input
              type="date"
              value={formData.complementaryHealth.endDate}
              onChange={(e) => handleInputChange('complementaryHealth.endDate', e.target.value)}
              className={styles.formControl}
            />
          </div>

          <div className={styles.sectionTitle}>Malpraktis Sigortası</div>
          
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              <FaCalendarAlt style={{ marginRight: '5px' }} /> Başlangıç Tarihi:
            </label>
            <input
              type="date"
              value={formData.malpractice.startDate}
              onChange={(e) => handleInputChange('malpractice.startDate', e.target.value)}
              className={styles.formControl}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              <FaCalendarAlt style={{ marginRight: '5px' }} /> Bitiş Tarihi:
            </label>
            <input
              type="date"
              value={formData.malpractice.endDate}
              onChange={(e) => handleInputChange('malpractice.endDate', e.target.value)}
              className={styles.formControl}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Maliyet (₺):</label>
            <input
              type="text"
              value={formData.cost}
              onChange={(e) => handleInputChange('cost', e.target.value)}
              placeholder="Maliyet"
              className={`${styles.formControl} ${errors.cost ? styles.formError : ''}`}
            />
            {errors.cost && (
              <div className={styles.errorMessage}>{errors.cost}</div>
            )}
          </div>

          <div className={styles.buttonGroup}>
            <button 
              onClick={handleClose} 
              className={styles.buttonCancel}
            >
              İptal
            </button>
            <button 
              onClick={handleSubmit} 
              className={styles.buttonSubmit}
            >
              Güncelle
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditInsuranceModal; 